import CollapsibleBreadcrumb from "@components/Breadcrumbs/CollapsibleBreadcrumb";
import ArticleWidthResizer from "@components/Layouts/CatalogLayout/ArticleLayout/ArticleWidthResizer";
import { EXPANDED_RIGHT_NAVIGATION_CONTENT_CLASS_NAME } from "@components/Layouts/CatalogLayout/RightNavigation/constants";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { cn } from "@core-ui/utils/cn";
import { getArticleItemLinkPath } from "@ext/article/LinkCreator/logic/getArticleItemLink";
import { DeleteItemButton } from "@ext/item/actions/DeleteItemButton";
import ItemMenu from "@ext/item/EditMenu";
import { useCurrentItemActions } from "@ext/item/useCurrentItemActions";
import t from "@ext/localization/locale/translate";
import { isGroupNode } from "@ext/navigation/catalog/SidebarNavigation/utils/isGroupNode";
import NavigationDropdown from "@ext/navigation/components/NavigationDropdown";
import type { ItemLink } from "@ext/navigation/NavigationLinks";
import { IconButton } from "@ui-kit/Button";
import { ComponentVariantProvider } from "@ui-kit/Providers";
import { useMemo, useRef } from "react";

const actionsClassName = cn(
	"absolute right-0 bottom-[-1em] mr-1 z-[1] flex items-center justify-end",
	"opacity-50 hover:opacity-100",
);

interface ArticleBreadcrumbProps {
	itemLinks: ItemLink[];
	hasPreview: boolean;
	className?: string;
	showActions?: boolean;
}

const ArticleBreadcrumb = ({ className, itemLinks, hasPreview, showActions = true }: ArticleBreadcrumbProps) => {
	const breadcrumbRef = useRef<HTMLDivElement>(null);

	const articlePath = useArticlePropsStore((state) => state.data.ref.path);

	const breadcrumbItems = useMemo(
		() =>
			getArticleItemLinkPath(itemLinks, articlePath)
				.slice(0, -1)
				.filter((link) => !isGroupNode(link))
				.map((link) => ({ title: link.title, url: link.pathname })),
		[itemLinks, articlePath],
	);

	const { itemLink, setItemLink, isDeleteOnly, isMenuVisible } = useCurrentItemActions(itemLinks, showActions);

	return (
		<ComponentVariantProvider variant="glass">
			<div
				className={cn(
					"article-breadcrumb relative flex flex-nowrap items-center max-sm:mb-1 print:hidden",
					hasPreview && "w-[68%]",
					className,
				)}
				ref={breadcrumbRef}
			>
				{breadcrumbItems.length > 0 && <CollapsibleBreadcrumb items={breadcrumbItems} />}
				{isMenuVisible && (
					<div className={EXPANDED_RIGHT_NAVIGATION_CONTENT_CLASS_NAME}>
						<div className={actionsClassName} data-qa="qa-article-actions">
							{isDeleteOnly ? (
								// Placed as NavigationDropdown places its trigger: where «…» stands in other catalogs.
								<DeleteItemButton itemLink={itemLink}>
									<IconButton
										className={actionsClassName}
										icon="trash"
										iconClassName="size-4"
										size="sm"
										style={{ marginRight: "-2px" }}
										variant="ghost"
									/>
								</DeleteItemButton>
							) : (
								<NavigationDropdown
									className={actionsClassName}
									style={{ marginRight: "-2px" }}
									tooltipText={t("article.actions.title")}
									trigger={
										<IconButton
											aria-label={t("article.actions.title")}
											icon="ellipsis"
											iconClassName="size-4"
											size="sm"
											variant="ghost"
										/>
									}
								>
									<ItemMenu itemLink={itemLink} setItemLink={setItemLink} />
								</NavigationDropdown>
							)}
						</div>
					</div>
				)}
				{!hasPreview && <ArticleWidthResizer />}
			</div>
		</ComponentVariantProvider>
	);
};

export default ArticleBreadcrumb;
